import { styled } from '@mui/material/styles'
import { useCallback } from 'react'
import { IconButton, Typography } from '@mui/material'
import { LIMITS } from '@weblite/permission'
import { ArrowRightIcon, PhoneVerificationIcon } from '$common/icons'
import { PrimaryButton } from '$common/buttons'
import { AuthContainer } from '$common/auth-container'
import { VerificationCodeInput } from '$common/verification-code-input'
import { Alert } from '$common/input/alert'
import { VerificationSession, VerifyError } from '$logic/auth'
import { closeAuthVerify, goToAuthSignin } from '$logic/router'
import { formatDuration } from '$func/time'
import { t } from '$i18n'
import { toHumanReadablePhone } from '$func/phone'
import { useVerifyPhoneForm } from '$comp/verification/use-verify-phone-form'

export function Verification({ phone, sessionId }: VerificationSession) {
  const {
    handleCodeChange,
    handleResendCode,
    resendMaxed,
    remainingMillis,
    alertText,
    loading,
  } = useVerifyPhoneForm({
    phone,
    sessionId,
    onCloseVerification: closeAuthVerify,
  })

  const handleGoBack = useCallback(async () => {
    await goToAuthSignin()
  }, [])

  // TODO get default value of input from prop
  return (
    <AuthContainer>
      <Header>
        <IconButton
          edge="end"
          sx={{ color: 'neutral500' }}
          onClick={handleGoBack}
        >
          <ArrowRightIcon />
        </IconButton>
        <Typography variant="h3">{t('signin.verification.title')}</Typography>
      </Header>
      <Body>
        <PhoneVerificationIcon
          sx={{
            color: 'secondary700',
            width: '80px',
            height: '80px',
            marginBottom: '12px',
          }}
        />
        <Typography color="secondary700" variant="h3" mb="8px">
          {toHumanReadablePhone(phone || '')}
        </Typography>
        <Typography color="secondary700" variant="body2" mb="32px">
          {t('signin.verification.description')()}
        </Typography>
        <OTPForm>
          <VerificationCodeInput
            timeout={LIMITS.verificationCode.resendInSeconds * 1000}
            resendMaxed={resendMaxed}
            numFields={LIMITS.verificationCode.length}
            onChange={handleCodeChange}
            hasError={Boolean(alertText)}
            autoComplete
          />
          <Alert
            variant="error"
            text={alertText}
            sxStyle={{
              display: alertText ? 'static' : 'none',
            }}
          />

          <PrimaryButton
            disabled={loading || resendMaxed || remainingMillis > 0}
            sx={{ marginTop: '32px' }}
            fullWidth
            text={
              resendMaxed || remainingMillis <= 0
                ? t('signin.verification.resendCode')
                : t('signin.verification.validTillXMore', {
                    x: formatDuration(remainingMillis),
                  })
            }
            onClick={handleResendCode}
            loading={loading}
          />
        </OTPForm>
      </Body>
    </AuthContainer>
  )
}

const Header = styled('div')`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin: 16px 0 22px;
`

const Body = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  margin: 0;
  padding: 0 8px;
`

const OTPForm = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`
